import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog, DialogContent,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import CircleIcon from "@mui/icons-material/Circle";
import React, {useEffect, useState} from "react";
import {useAppDispatch} from "../../redux/hooks";
import {Meter, Property, residentPortalApi as api} from "../../redux/residentPortalApi";
import GasMeterIcon from "@mui/icons-material/GasMeter";
import WaterIcon from "@mui/icons-material/Water";
import BoltIcon from "@mui/icons-material/Bolt";
import {makeStyles} from "@mui/styles";
import {theme} from "../../theme/theme";
import MeterDetailsModel from "./MeterDetailsModel";
import TopUpModal from "./TopUpModal";

const useStyles = makeStyles({
  sectionTitle: {
    margin: theme.spacing(2, 0),
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  card: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
    position: 'relative',
    height: '100%',
  },
  icon: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    width: theme.spacing(10),
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(2),
  },
  balanceText: {
    fontWeight: 600,
    fontSize: '1.7rem',
  },
  readingWithUnit: {
    fontWeight: 400,
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  disabledCard: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.text.disabled,
  },
  statusIconContainer: {
    position: 'absolute',
    top: theme.spacing(2),
    left: theme.spacing(2),
    borderRadius: '50%',
    padding: theme.spacing(0.5),
    border: `2px solid ${theme.palette.background.paper}`,
    backgroundColor: theme.palette.background.default, // Match background
    zIndex: 2, // Ensure it's above other elements
  },
  activeStatusIcon: {
    color: 'green',
  },
  inactiveStatusIcon: {
    color: 'red',
  },
});

interface OwnProps {
  property: Property;
  outstandingLeviesBalance: number;
}

interface MeterData {
  utility: string;
  meter: Meter | null;
  data?: any;
  isLoading: boolean;
  error: any;
}

const MetersSection: React.FC<OwnProps> = ({property, outstandingLeviesBalance}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();

  const utilities: string[] = ['water', 'electricity', 'gas'];
  const meters = property?.meters || []; // Default meters to an empty array

  const [meterDataArray, setMeterDataArray] = useState<MeterData[]>(() =>
    utilities.map((utility) => ({
      utility,
      meter: meters.find(m => m.service === utility) || null,
      data: null,
      isLoading: true,
      error: null,
    }))
  );

  const [selectedMeter, setSelectedMeter] = useState<Meter | null>(null);
  const [openModal, setOpenModal] = useState(false);

  const [openTopUpDialog, setOpenTopUpDialog] = useState(false);

  const handleCloseDialog = () => setOpenTopUpDialog(false);

  const costOfElectricity = 0.25;

  const handleOpenDetails = (meter: Meter) => {
    setSelectedMeter(meter);
    setOpenModal(true);
  };

  const handleCloseDetails = () => {
    setSelectedMeter(null);
    setOpenModal(false);
  };

  useEffect(() => {
    const fetchMeterData = async (meterIndex: number, imei: string) => {
      try {
        const result = await dispatch(api.endpoints.getMeterReadByImei.initiate({ imei }));
        setMeterDataArray(prev => {
          const updated = [...prev];
          updated[meterIndex] = {
            ...updated[meterIndex],
            data: result?.data,
            isLoading: false,
            error: null,
          };
          return updated;
        });
      } catch (error) {
        console.error('Error fetching meter data:', error);
        setMeterDataArray(prev => {
          const updated = [...prev];
          updated[meterIndex] = {
            ...updated[meterIndex],
            isLoading: false,
            error: 'Error fetching data',
          };
          return updated;
        });
      }
    };

    meterDataArray.forEach((meterData, index) => {
      if (meterData.meter) {
        fetchMeterData(index, meterData.meter.imei);
      } else {
        setMeterDataArray(prev => {
          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            isLoading: false,
            error: 'No meter installed',
          };
          return updated;
        });
      }
    });
  //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleTopUpClick = (meter: Meter) => {
    setSelectedMeter(meter);
    setOpenTopUpDialog(true);
  };


  const getReadingUnit = (utility: string) => {
    switch (utility) {
      case 'water':
        return 'm³';
      case 'electricity':
        return 'kWh';
      case 'gas':
        return 'm³';
      default:
        return '';
    }
  };

  const iconGetter = (serviceName: string) => {
    switch (serviceName) {
      case 'gas':
        return <GasMeterIcon/>;
      case 'water':
        return <WaterIcon/>;
      case 'electricity':
        return <BoltIcon/>;
      default:
        return null;
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        border: '1px solid rgba(0, 0, 0, 0.05)',
        mb: 4
      }}
    >
      <Stack spacing={3}>
        <Typography variant="h2" sx={{
          fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.75rem' },
          fontWeight: 600,
          color: '#1976d2',
          mb: 2,
          borderBottom: '2px solid #e0e0e0',
          paddingBottom: 1
        }}>
          Meters
        </Typography>
        
        <Grid container spacing={3}>
          {meterDataArray.map(({utility, meter, data, isLoading, error}) => (
            <Grid item xs={12} sm={6} md={4} key={utility}>
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                  border: '1px solid #e5e7eb',
                  background: (!meter || (!isLoading && !data)) ? '#f5f5f5' : 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
                  backgroundColor: (!meter || (!isLoading && !data)) ? '#f5f5f5' : '#ffffff',
                  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }
                }}
              >
                <Avatar 
                  sx={{
                    backgroundColor: '#1976d2',
                    color: 'white',
                    width: 56,
                    height: 56,
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    zIndex: 1,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                  }}
                >
                  {iconGetter(utility)}
                </Avatar>
                <CardContent sx={{ p: { xs: 2, sm: 3 }, pt: { xs: 5, sm: 6 }, pr: { xs: 3, sm: 5 }, pl: { xs: 2.5, sm: 4 } }}>
                  {data && (
                    <Box className={classes.statusIconContainer}>
                      <CircleIcon
                        className={data.valveState ? classes.activeStatusIcon : classes.inactiveStatusIcon}
                        fontSize="small"
                      />
                    </Box>
                  )}
                  {isLoading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3, gap: 1, mt: { xs: 10, sm: 15 } }}>
                      <CircularProgress size={40} thickness={4} sx={{ color: 'primary.main' }}/>
                      <Typography variant="body2" color="primary" sx={{ fontWeight: 'medium', mt: 1 }}>
                        Loading meter data...
                      </Typography>
                    </Box>
                  ) : error ? (
                    <Box sx={{ p: 2 }}>
                      <Typography color="error" variant="body2" sx={{ fontWeight: 'medium' }}>{error}</Typography>
                      <Typography color="text.secondary" variant="caption" sx={{ display: 'block', mt: 1 }}>
                        Please try again later
                      </Typography>
                    </Box>
                  ) : (

                    <Box mt={{ xs: 10, sm: 15 }}>
                      {meter && meter.service !== 'electricity' && (data?.['remaining flow'] ?? data?.remainingAmount) !== undefined && (
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.2rem', sm: '1.7rem' }, mb: 1, mt: { xs: 3, sm: 5 } }}>
                          Balance: {Number(data['remaining flow'] ?? data.remainingAmount).toFixed(2)} {getReadingUnit(utility)}
                        </Typography>
                      )}
                      {meter && meter.service === 'electricity' && data?.remainingAmount !== undefined && (
                        <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.2rem', sm: '1.7rem' }, mb: 1, mt: { xs: 3, sm: 5 } }}>
                          Balance: {Number(data.remainingAmount).toFixed(2)} {getReadingUnit(utility)} 
                        </Typography>
                      )}
                      {meter && meter.service !== 'electricity' && data?.reading !== undefined && (
                        <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' }, color: 'text.secondary', mt: 1 }}>
                          Total Consumption: {Number(data.reading).toFixed(2)} {getReadingUnit(utility)}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                    {isLoading ? '' : (data && meter) ? 'Service is active' : 'Service is currently unavailable'}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, gap: 2 }}>
                    {/* <Button
                      variant="contained"
                      color="primary"
                      disabled={!meter || !data}
                      onClick={() => meter && handleOpenDetails(meter)}
                      sx={{ 
                        borderRadius: 1.5,
                        textTransform: 'none',
                        fontWeight: 500
                      }}
                    >
                      View Details
                    </Button> */}
                  </Box>
                  <Tooltip
                    title={
                      outstandingLeviesBalance > 0
                        ? 'You must pay your outstanding levy balance first'
                        : ''
                    }
                    arrow
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Button
                        onClick={() => meter && handleTopUpClick(meter)}
                        disabled={!meter || !data || outstandingLeviesBalance > 0}
                        sx={{ 
                          borderRadius: 1.5,
                          textTransform: 'none',
                          fontWeight: 500
                        }}
                      >
                        Top Up
                      </Button>
                    </Box>
                  </Tooltip>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Stack>
      
      {/* Top Up Dialog */}
      <Dialog open={openTopUpDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogContent>
          <TopUpModal
            selectedMeter={selectedMeter}
            propertyId={property.id ?? ""}
            purpose={selectedMeter?.service ?? ""}
          />
        </DialogContent>
      </Dialog>

      <MeterDetailsModel
        open={openModal}
        onClose={handleCloseDetails}
        meter={selectedMeter}
      />
    </Paper>
  )
}

export default MetersSection;